import { useEffect, useState } from 'react';
import { Mail, Phone} from 'lucide-react';
import contactService from '../services/contactService';
import {SocialMediaLinks} from '../components/SocialMediaLinks';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import '../styles/ContactPage.css';

export function ContactPage() {
  const [contactInfo, setContactInfo] = useState({
    public_email: 'contact@getfitwithdaniella.com',
    public_phone: '',
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  useEffect(() => {
    const fetchContact = async () => {
      try {
        const data = await contactService.getContact();
        if (data) {
          setContactInfo({
            public_email: data.public_email || 'contact@getfitwithdaniella.com',
            public_phone: data.public_phone || '',
          });
        }
      } catch (error) {
        console.error('Failed to load contact info', error);
      } finally {
        setLoading(false);
      }
    };

    fetchContact();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitStatus(null);
    try {
      const result = await contactService.submitInquiry(formData);
      setSubmitStatus({
        type: 'success',
        message: result?.message || "Thanks — we've received your message.",
      });
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      const apiMessage =
        error?.response?.data?.message ||
        error?.response?.data?.errors?.[0]?.message ||
        'Could not send your message. Please try again.';
      setSubmitStatus({ type: 'error', message: apiMessage });
    } finally {
      setSubmitting(false);
    }
  };

  const cleanPhoneForTel = (phone = '') => phone.replace(/[^\d+]/g, '');

  const emailLink = loading ? '#' : `mailto:${contactInfo.public_email}`;
  const phoneLink = contactInfo.public_phone
    ? `tel:${cleanPhoneForTel(contactInfo.public_phone)}`
    : '#';

  return (
    <div className="contact-page">
      <HelmetProvider>
        <Helmet>
          <title>
            Contact - Get Fit with Daniella
          </title>

          <meta
            name="description"
            content="Get in touch with Daniella for questions about fitness programs, coaching, and getting started on your fitness journey."
          />

          <link rel="canonical" href="https://getfitwithdaniella.com/contact/" />

          {/* Open Graph */}
          <meta
            property="og:title"
            content="Contact - Get Fit with Daniella"
          />

          <meta
            property="og:description"
            content="Get in touch with Daniella for questions about fitness programs, coaching, and getting started on your fitness journey."
          />

          <meta
            property="og:url"
            content="https://getfitwithdaniella.com/contact/"
          />

          <meta property="og:type" content="website" />

          <meta
            property="og:site_name"
            content="Get Fit with Daniella"
          />

          <meta
            property="og:image"
            content="https://getfitwithdaniella.com/assets/og-image.jpg"
          />

          {/* Twitter / X */}
          <meta
            name="twitter:card"
            content="summary_large_image"
          />

          <meta
            name="twitter:title"
            content="Contact - Get Fit with Daniella"
          />

          <meta
            name="twitter:description"
            content="Get in touch with Daniella for questions about fitness programs, coaching, and getting started on your fitness journey."
          />

          <meta
            name="twitter:image"
            content="https://getfitwithdaniella.com/assets/og-image.jpg"
        />
        </Helmet>
      </HelmetProvider>
      <div className="contact-card">
        <div className="contact-info-section">
          <h1>Get in touch</h1>
          <p className="contact-description">
            If you have any questions regarding our Services or need help, please fill out the form here. 
            We do our best to respond within 1 business day.
          </p>
          <div className="contact-info-items">
            <div className="info-item">
              <div className="info-icon-wrapper">
                <Mail className="info-icon" size={20} />
              </div>
              <div className="info-text">
                <p className="info-label">Email</p>
                <p className="info-value">
                  {loading ? (
                    'Loading...'
                  ) : (
                    <a href={emailLink} className="contact-link">
                      {contactInfo.public_email}
                    </a>
                  )}
                </p>
              </div>
            </div>

            <div className="info-item">
              <div className="info-icon-wrapper">
                <Phone className="info-icon" size={20} />
              </div>
              <div className="info-text">
                <p className="info-label">Phone</p>
                <p className="info-value">
                  {loading ? (
                    'Loading...'
                  ) : (
                    <a href={phoneLink} className="contact-link">
                      {contactInfo.public_phone || '—'}
                    </a>
                  )}
                </p>
              </div>
            </div>
          </div>
          <SocialMediaLinks hasDescription={true} />
        </div>

        <div className="contact-form-section">
          <form onSubmit={handleSubmit} className="contact-form">
            <div className="form-field">
              <label htmlFor="name">Name</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-field">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-field">
              <label htmlFor="phone">Phone</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-field">
              <label htmlFor="message">Message</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows="5"
                required
              />
            </div>

            <button type="submit" className="submit-button" disabled={submitting}>
              {submitting ? 'Sending…' : 'Submit'}
            </button>
            {submitStatus && (
              <p
                className={
                  submitStatus.type === 'success'
                    ? 'form-status form-status-success'
                    : 'form-status form-status-error'
                }
                role="status"
              >
                {submitStatus.message}
              </p>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}

